import moment from 'moment';
import { Check, X } from 'lucide-react';
import { toast } from 'sonner';
import ProfileImage from '@/client/components/common/ProfileImage';
import UserRowActions from '@/client/components/admin/UsersAndPermissions/UserRowActions';
import type { UserAccountRole, UserAccountRoleType } from '@/client/types/admin/users-orgs';
import { PhoneVerBadge } from './PhoneVerBadge';
import { PersonaBadge } from './PersonaBadge';
import { MAX_ROLE_BADGES, STATUS_BADGE, genderLabel, roleLabel, userPhotoUrl, userStatus } from './helpers';

export const UserTableRow: React.FC<{
  user: any;
  roles: UserAccountRole[];
  roleTypeById: Map<number, UserAccountRoleType>;
  onClick: (uid: number) => void;
  selected: boolean;
  onToggle: (uid: number) => void;
}> = ({ user, roles, roleTypeById, onClick, selected, onToggle }) => (
  <tr
    onClick={() => onClick(user.uid)}
    className={`cursor-pointer ${selected ? 'bg-blue-50/60 hover:bg-blue-50' : 'hover:bg-gray-50'}`}
  >
    {/* The row navigates; the checkbox cell must not. */}
    <td className="w-10 px-4 py-4" onClick={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        className="h-4 w-4 cursor-pointer rounded border-gray-300"
        checked={selected}
        onChange={() => onToggle(user.uid)}
        aria-label={`Select ${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()}
      />
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center">
        <div className="flex-shrink-0 h-10 w-10">
          <ProfileImage
            name={`${user.first_name ?? ''} ${user.last_name ?? ''}`}
            src={userPhotoUrl(user.photo)}
          />
        </div>
        <div className="ml-4">
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
            {user.first_name} {user.last_name}
            {user.persona === 1 && <PersonaBadge />}
          </div>
          <div className="text-xs text-gray-400" onClick={(e) => e.stopPropagation()}>
            <CopyId label="uid" value={String(user.uid)} />
            {' · '}
            {user.ffid ? <CopyId label="ffid" value={user.ffid} /> : 'ffid —'}
          </div>
        </div>
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm">
      <div className="text-gray-900">{user.email1 ?? '—'}</div>
      {user.email1 && user.email1_ver !== 1 && <div className="text-xs text-amber-600">Unverified</div>}
    </td>
    {/* phone2 is often the WhatsApp/next-of-kin line, so keep it visible. */}
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center gap-1 text-sm text-gray-900">
        {user.phone1 ?? '—'}
        {user.phone1 && <PhoneVerBadge verified={user.phone1_ver === 1} />}
      </div>
      {user.phone2 ? (
        <div className="flex items-center gap-1 text-xs text-gray-500">
          {user.phone2}
          <PhoneVerBadge verified={user.phone2_ver === 1} />
        </div>
      ) : (
        <span className="text-xs text-gray-400">No 2nd phone</span>
      )}
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm">
      <div className="text-gray-900">{genderLabel(user.gender)}</div>
      <div className="text-xs text-gray-500">
        {user.dob ? `${moment().diff(moment(user.dob), 'years')} yrs old` : 'Age unknown'}
        {user.country_iso2 ? ` · ${user.country_iso2}` : ''}
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      {user.comp_rate != null ? (
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-200">
            <div
              className={`h-full rounded-full ${
                user.comp_rate >= 80 ? 'bg-emerald-500' : user.comp_rate >= 40 ? 'bg-amber-500' : 'bg-red-500'
              }`}
              style={{ width: `${Math.min(100, Math.max(0, user.comp_rate))}%` }}
            />
          </div>
          <span className="text-xs font-medium text-gray-600">{user.comp_rate}%</span>
        </div>
      ) : (
        <span className="text-xs text-gray-400">—</span>
      )}
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex flex-wrap gap-1">
        {[
          { label: 'Email', ok: user.email1_ver === 1 },
          { label: 'Phone', ok: user.phone1_ver === 1 },
          { label: 'ID', ok: user.verified_id === 1 },
        ].map(({ label, ok }) => (
          <span
            key={label}
            className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${
              ok ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
            }`}
            title={ok ? `${label} verified` : `${label} not verified`}
          >
            {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
            {label}
          </span>
        ))}
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      {(() => {
        const badge = STATUS_BADGE[userStatus(user)];
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
            title={badge.title}
          >
            {badge.label}
          </span>
        );
      })()}
    </td>
    <td className="px-6 py-4">
      {roles?.length ? (
        <div className="flex flex-wrap items-center gap-1 max-w-[240px]">
          {roles.slice(0, MAX_ROLE_BADGES).map((role) => {
            const label = roleLabel(role, roleTypeById);
            return (
              <span
                key={role.uarid}
                className={`inline-flex max-w-[150px] items-center truncate rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${
                  role.active === 1 ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-400 line-through'
                }`}
                title={role.active === 1 ? label : `${label} (inactive)`}
              >
                {label}
              </span>
            );
          })}
          {roles.length > MAX_ROLE_BADGES && (
            <span
              className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600"
              title={roles.slice(MAX_ROLE_BADGES).map((r) => roleLabel(r, roleTypeById)).join(', ')}
            >
              +{roles.length - MAX_ROLE_BADGES}
            </span>
          )}
        </div>
      ) : (
        <span className="text-xs text-gray-400">—</span>
      )}
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm" title={user.date_added ?? undefined}>
      <div className="text-gray-900">{user.date_added ? moment(user.date_added).format('D MMM YYYY') : '—'}</div>
      {user.date_added && <div className="text-xs text-gray-400">{moment(user.date_added).fromNow()}</div>}
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-right">
      <UserRowActions row={{ user, roles }} />
    </td>
  </tr>
);

function CopyId({ label, value }: { label: string; value: string }) {
  return (
    <button
      type="button"
      title={`Copy ${label}`}
      className="cursor-copy hover:text-gray-700"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => toast.success(`Copied ${label}`));
      }}
    >
      {label} {value}
    </button>
  );
}
